/**
 * Centralized Logger
 * 
 * Production-safe logging that:
 * - Only logs in development mode
 * - Provides structured log levels
 * - Can be extended for remote logging
 * 
 * Usage:
 *   import { logger } from '@/lib/logger';
 *   
 *   logger.debug('Fetching data...', { userId });
 *   logger.info('User logged in', { email });
 *   logger.warn('Rate limit approaching');
 *   logger.error('Failed to load', error);
 */

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LogPayload {
  level: LogLevel;
  message: string;
  data?: unknown;
  timestamp: string;
}

/**
 * Check if we're in development mode
 */
function isDev(): boolean {
  try {
    return (import.meta as ImportMeta & { env?: { DEV?: boolean } }).env?.DEV === true;
  } catch {
    return false;
  }
}

/**
 * Format log message with optional data
 */
function formatLog(level: LogLevel, message: string, data?: unknown): LogPayload {
  return {
    level,
    message,
    data,
    timestamp: new Date().toISOString(),
  };
}

/**
 * Output to console (dev only)
 */
function output(payload: LogPayload): void {
  if (!isDev()) return;

  const prefix = `[${payload.level.toUpperCase()}]`;
  const args: unknown[] = [prefix, payload.message];
  
  if (payload.data !== undefined) {
    args.push(payload.data);
  }

  switch (payload.level) {
    case 'debug':
      console.debug(...args);
      break;
    case 'info':
      console.info(...args);
      break;
    case 'warn':
      console.warn(...args);
      break;
    case 'error':
      console.error(...args);
      break;
  }
}

export const logger = {
  /**
   * Debug level - development only, stripped in production
   * Supports multiple arguments like console.log
   */
  debug(message: string, ...args: unknown[]): void {
    if (!isDev()) return;
    console.debug('[DEBUG]', message, ...args);
  },

  /**
   * Info level - general information
   */
  info(message: string, ...args: unknown[]): void {
    if (!isDev()) return;
    console.info('[INFO]', message, ...args);
  },

  /**
   * Warn level - potential issues
   */
  warn(message: string, ...args: unknown[]): void {
    if (!isDev()) return;
    console.warn('[WARN]', message, ...args);
  },

  /**
   * Error level - errors
   * For user-facing errors, use errorTracker instead
   */
  error(message: string, ...args: unknown[]): void {
    // Always log errors in dev
    if (isDev()) {
      console.error('[ERROR]', message, ...args);
    }
    // In production, errors should go through errorTracker
  },

  /**
   * Group related logs (dev only)
   */
  group(label: string, fn: () => void): void {
    if (!isDev()) {
      fn();
      return;
    }
    console.group(label);
    fn();
    console.groupEnd();
  },

  /**
   * Time an operation (dev only)
   */
  time<T>(label: string, fn: () => T): T {
    if (!isDev()) {
      return fn();
    }
    console.time(label);
    const result = fn();
    console.timeEnd(label);
    return result;
  },

  /**
   * Time an async operation (dev only)
   */
  async timeAsync<T>(label: string, fn: () => Promise<T>): Promise<T> {
    if (!isDev()) {
      return fn();
    }
    console.time(label);
    const result = await fn();
    console.timeEnd(label);
    return result;
  },
};

export default logger;

